import { useQueryClient } from '@tanstack/react-query';
import {
  OperationLog,
  SharedDatasetManagerDatasetSchemaEncoderSchemaViolation as SchemaViolation,
} from '@rossum/mdh-api-client';
import { getErrorMessage } from '../getErrorMessage';
import { useDatasetUpload } from '../hooks/useDatasetUpload';
import { useOperation } from '../hooks/useOperation';
import {
  isOperationInProgress,
  OPERATIONS_QUERY,
} from '../hooks/useOperations';

// `file`: the file has to change before it is sent again. `request`: sending
// it again may work.
export type UploadFailure =
  | { kind: 'file'; violations: SchemaViolation[]; message: string | undefined }
  | { kind: 'request'; message: string | undefined };

// Why the upload failed, or null while it is sent, imported or done. Failing
// to poll the operation is not a failure: polling goes on.
export const getFailure = (
  uploadError: unknown,
  operation: OperationLog | undefined
): UploadFailure | null => {
  if (uploadError) {
    return { kind: 'request', message: getErrorMessage(uploadError) };
  }
  if (
    operation === undefined ||
    isOperationInProgress(operation) ||
    operation.status === 'FINISHED'
  ) {
    return null;
  }
  const violations = operation.metadata.schema_violations ?? [];
  const message = operation.message ?? undefined;
  return violations.length > 0 || operation.error_type === 'CLIENT_ERROR'
    ? { kind: 'file', violations, message }
    : { kind: 'request', message };
};

// Uploads a file and follows the import it starts, so why it failed can be
// shown next to the file.
export const useFollowedUpload = ({
  onFinished,
}: {
  // The import finished, or, when MDH didn't say which import it started,
  // the file was sent and the import isn't followed.
  onFinished: (followed: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const {
    mutate,
    data: operationId,
    error,
    reset,
    isLoading,
  } = useDatasetUpload();
  const { data: operation } = useOperation(operationId ?? null, {
    onSuccess: polled => {
      if (isOperationInProgress(polled)) return;
      // So the Data tab shows how the import ended along with the dialog.
      queryClient.invalidateQueries({ queryKey: OPERATIONS_QUERY });
      if (polled.status === 'FINISHED') onFinished(true);
    },
  });

  const failure = getFailure(error, operation);
  const importing =
    isLoading || (operationId !== undefined && failure === null);

  // Callbacks stop once the component using this unmounts, so an import left
  // running is no longer followed.
  const upload = (payload: Parameters<typeof mutate>[0]) =>
    mutate(payload, {
      onSuccess: id => {
        if (id === undefined) onFinished(false);
      },
    });

  return { upload, sending: isLoading, importing, failure, reset };
};
